import { AccessorFn } from '@tanstack/react-table';
import { useCallback } from 'react';
import { AlgorithmTargetRuleType } from '../../types';
import { useIsDimensionWithDataType } from '../joins/utils';
import { getAlgorithmMappingLabel } from '../mappings/utils';
import { AlgorithmExtendedType } from '../schema/types';
import { AlgorithmSourceFormDataType } from '../sources/schema/types';
import {
  AlgorithmSource,
  getAlgorithmJoinLabels,
  getAlgorithmSourceLabel,
  getAlgorithmTargetMappingRuleItemLabel,
  getAlgorithmTargetMappingRuleLabels,
  getAlgorithmTargetRuleRuleItemLabel,
  getAlgorithmTargetRuleRuleLabels,
} from '../utils';

type MultilineAccessorType =
  | 'dimensions'
  | 'indicators'
  | 'sources'
  | 'mappings'
  | 'joins'
  | 'targetRules'
  | 'targetMapRules'
  | 'targetRuleRules';

const BETWEEN_APOSTROPHE = /^'.+?'$/;

const dimensionsAccessorFn: AccessorFn<AlgorithmExtendedType, string> = ({
  targets,
}) =>
  Array.from(
    new Set(
      (targets || [])
        .flatMap((target) => target.rules)
        .filter(
          (rule) =>
            rule.items &&
            rule.expression &&
            rule.expression.match(BETWEEN_APOSTROPHE),
        )
        .flatMap((rule) =>
          rule.items
            .filter((item) => item.dimensionId)
            .map((item) => `${item.dimensionCode} = ${rule.expression}`),
        ),
    ),
  ).join('\n');

const indicatorsAccessorFn: AccessorFn<AlgorithmExtendedType, string> = ({
  targets,
}) => {
  const uniqueIndicators = new Set<string>();
  return (targets || [])
    .flatMap((target) => target.rules)
    .flatMap((rule) => (rule.items || []).filter((item) => item.indicatorId))
    .map((item) => `${item.indicatorCode} - ${item.indicatorName}`)
    .filter((indicator) => {
      if (uniqueIndicators.has(indicator)) {
        return false;
      }

      uniqueIndicators.add(indicator);
      return true;
    })
    .join('\n');
};

const sourcesAccessorFn: AccessorFn<AlgorithmExtendedType, string> = ({
  sources,
}) => (sources || []).map(getAlgorithmSourceLabel).join('\n');

const useMappingsAccessorFn = (sourcesById: Map<number, AlgorithmSource>) =>
  useCallback<AccessorFn<AlgorithmExtendedType, string>>(
    ({ mappings }) =>
      (mappings || [])
        .map((mapping) => getAlgorithmMappingLabel(mapping, sourcesById))
        .join('\n'),
    [sourcesById],
  );

const useJoinsAccessorFn = (
  sourcesById: Map<number, AlgorithmSourceFormDataType>,
) => {
  const isDimensionWithDateDataType = useIsDimensionWithDataType('DATE');

  return useCallback<AccessorFn<AlgorithmExtendedType, string>>(
    ({ joins }) =>
      getAlgorithmJoinLabels(
        joins,
        isDimensionWithDateDataType,
        sourcesById,
      ).join('\n'),
    [sourcesById, isDimensionWithDateDataType],
  );
};

const useTargetRulesAccessorFn = (sourcesById: Map<number, AlgorithmSource>) =>
  useCallback<AccessorFn<AlgorithmExtendedType, string>>(
    ({ expression, targets }) => {
      if (!targets?.length) {
        return expression;
      }
      return targets[0].rules
        .flatMap((rule) =>
          rule.items?.map((item) => {
            return rule.ruleType === AlgorithmTargetRuleType.MAP
              ? getAlgorithmTargetMappingRuleItemLabel(
                  rule,
                  item,
                  sourcesById,
                  'M',
                )
              : getAlgorithmTargetRuleRuleItemLabel(rule, item, 'R');
          }),
        )
        .join('\n');
    },
    [sourcesById],
  );

const useTargetMapRulesAccessorFn = (
  sourcesById: Map<number, AlgorithmSource>,
) =>
  useCallback<AccessorFn<AlgorithmExtendedType, string>>(
    ({ targets }) => {
      if (!targets.length) {
        return '';
      }
      return getAlgorithmTargetMappingRuleLabels(
        targets[0].rules,
        sourcesById,
      ).join('\n');
    },
    [sourcesById],
  );

const useTargetRuleRulesAccessorFn = () =>
  useCallback<AccessorFn<AlgorithmExtendedType, string>>(({ targets }) => {
    if (!targets.length) {
      return '';
    }
    return getAlgorithmTargetRuleRuleLabels(targets[0].rules).join('\n');
  }, []);

export const useAlgorithmTableMultilineAccessors = (
  sourcesById: Map<number, AlgorithmSourceFormDataType>,
): Record<
  MultilineAccessorType,
  AccessorFn<AlgorithmExtendedType, string>
> => ({
  dimensions: dimensionsAccessorFn,
  indicators: indicatorsAccessorFn,
  sources: sourcesAccessorFn,
  mappings: useMappingsAccessorFn(sourcesById),
  joins: useJoinsAccessorFn(sourcesById),
  targetRules: useTargetRulesAccessorFn(sourcesById),
  targetMapRules: useTargetMapRulesAccessorFn(sourcesById),
  targetRuleRules: useTargetRuleRulesAccessorFn(),
});
